import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useT } from "@/i18n/LanguageProvider";

const baseImages = {
  savoiardi:
    "https://static.prod-images.emergentagent.com/jobs/3b406688-3aca-47ad-b466-823e6df8af3e/images/4904a11069100ca0a1e33de5a74efd2672ea1bb4187dc57676ecca027ccf9807.png",
  mascarpone:
    "https://static.prod-images.emergentagent.com/jobs/3b406688-3aca-47ad-b466-823e6df8af3e/images/e45df0a5825d01f53e4177ae25e51325e29b075b39f1eae3d0eeb5cdeb221c20.png",
  cocoa:
    "https://static.prod-images.emergentagent.com/jobs/3b406688-3aca-47ad-b466-823e6df8af3e/images/558a0598647a415dd1508d5d43d4cd72ded00434b0263f4e267687032ac65f03.png",
};

export default function Bases() {
  const { t } = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const x1 = useTransform(scrollYProgress, [0, 0.5], [-180, 0]);
  const x2 = useTransform(scrollYProgress, [0, 0.55], [180, 0]);
  const x3 = useTransform(scrollYProgress, [0, 0.6], [-220, 0]);
  const xs = [x1, x2, x3];

  const bases = [
    {
      key: "savoiardi",
      name: "Savoiardi",
      label: t("bases.savoiardi.label"),
      detail: t("bases.savoiardi.detail"),
      img: baseImages.savoiardi,
    },
    {
      key: "mascarpone",
      name: "Mascarpone",
      label: t("bases.mascarpone.label"),
      detail: t("bases.mascarpone.detail"),
      img: baseImages.mascarpone,
    },
    {
      key: "cocoa",
      name: "Cocoa Nero",
      label: t("bases.cocoa.label"),
      detail: t("bases.cocoa.detail"),
      img: baseImages.cocoa,
    },
  ];

  return (
    <section
      id="bases"
      ref={ref}
      data-testid="bases-section"
      className="relative w-full py-28 md:py-40 grain"
      style={{ background: "#211512" }}
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12">
        <div className="max-w-3xl">
          <p
            className="label-tracked"
            style={{ color: "#C5A059" }}
            data-testid="bases-eyebrow"
          >
            {t("bases.eyebrow")}
          </p>
          <h2
            className="font-serif font-light text-[#FFF8EE] mt-6 leading-[1]"
            style={{ fontSize: "clamp(2.4rem, 5.6vw, 5rem)" }}
            data-testid="bases-headline"
          >
            {t("bases.headline_1")}
            <br />
            <span className="italic" style={{ color: "#C5A059" }}>
              {t("bases.headline_2")}
            </span>
          </h2>
        </div>

        <div className="mt-20 hidden md:grid grid-cols-3 gap-8">
          {bases.map((b, i) => (
            <motion.article
              key={b.key}
              style={{ x: xs[i] }}
              className="group relative"
              data-testid={`base-card-${b.key}`}
            >
              <div className="relative overflow-hidden aspect-[3/4] bg-[#2d1d18]">
                <motion.img
                  src={b.img}
                  alt={b.name}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.04 }}
                  transition={{ duration: 1.2, ease: [0.2, 0.8, 0.2, 1] }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#211512] via-transparent to-transparent opacity-60" />
              </div>
              <div className="mt-6">
                <p className="label-tracked text-[#C5A059]">{b.label}</p>
                <h3 className="font-serif text-[#FFF8EE] text-4xl mt-2">
                  {b.name}
                </h3>
                <p className="text-[#FFF8EE]/70 mt-3 max-w-sm">{b.detail}</p>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 gap-12 md:hidden">
          {bases.map((b, i) => (
            <motion.article
              key={b.key}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, delay: i * 0.12 }}
              data-testid={`base-card-mobile-${b.key}`}
            >
              <div className="relative overflow-hidden aspect-[3/4] bg-[#2d1d18]">
                <img
                  src={b.img}
                  alt={b.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#211512] via-transparent to-transparent opacity-60" />
              </div>
              <div className="mt-6">
                <p className="label-tracked text-[#C5A059]">{b.label}</p>
                <h3 className="font-serif text-[#FFF8EE] text-3xl mt-2">
                  {b.name}
                </h3>
                <p className="text-[#FFF8EE]/70 mt-3">{b.detail}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
